import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SetURLSearchParams } from 'react-router-dom';
import type { PlaceSuggestion } from '../domain';
import { resolvePlaceDestination } from '../application';

interface SearchBarFields {
  destination: string;
  destinationId: string;
  ds: string;
  de: string;
  ad: string;
  ch: string;
  pt: string;
}

interface SearchBarProps {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export function SearchBar({ searchParams, setSearchParams }: SearchBarProps) {
  const { register, handleSubmit, setValue, watch } = useForm<SearchBarFields>({
    defaultValues: {
      destination: searchParams.get('destination') ?? '',
      destinationId: searchParams.get('destinationId') ?? '',
      ds: searchParams.get('ds') ?? '',
      de: searchParams.get('de') ?? '',
      ad: searchParams.get('ad') ?? '1',
      ch: searchParams.get('ch') ?? '0',
      pt: searchParams.get('pt') ?? '0',
    },
  });

  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [guestDropdownOpen, setGuestDropdownOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const guestDropdownRef = useRef<HTMLDivElement>(null);

  const adults = parseInt(watch('ad') || '1', 10);
  const children = parseInt(watch('ch') || '0', 10);
  const pets = parseInt(watch('pt') || '0', 10);
  const checkIn = watch('ds');

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(e.target as Node)) {
        setGuestDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleDestinationChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setValue('destinationId', '');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoadingSuggestions(false);
      return;
    }
    setLoadingSuggestions(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await resolvePlaceDestination(q);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);
  }

  function handleSuggestionSelect(place: PlaceSuggestion) {
    setValue('destination', place.name);
    setValue('destinationId', place.identifier);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function changeGuests(type: 'adults' | 'children' | 'pets', delta: number) {
    if (type === 'adults') {
      const next = Math.max(1, Math.min(16, adults + delta));
      setValue('ad', String(next));
    } else if (type === 'children') {
      const next = Math.max(0, Math.min(5, children + delta));
      setValue('ch', String(next));
    } else {
      const next = Math.max(0, Math.min(5, pets + delta));
      setValue('pt', String(next));
    }
  }

  function onSubmit(data: SearchBarFields) {
    const next = new URLSearchParams(searchParams);
    next.set('destination', data.destination);
    if (data.destinationId) next.set('destinationId', data.destinationId);
    else next.delete('destinationId');
    if (data.ds) next.set('ds', data.ds);
    if (data.de) next.set('de', data.de);
    next.set('ad', data.ad || '1');
    next.set('ch', data.ch || '0');
    next.set('pt', data.pt || '0');
    setSearchParams(next);
  }

  const parts = [`${adults} adult${adults !== 1 ? 's' : ''}`, `${children} child${children !== 1 ? 'ren' : ''}`];
  if (pets > 0) parts.push(`${pets} pet${pets !== 1 ? 's' : ''}`);
  const guestLabel = parts.join(', ');

  return (
    <div
      className="lift p-6 lg:p-8 mb-8"
      style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5"
      >
        <div className="lg:col-span-2" ref={suggestionsRef} style={{ position: 'relative' }}>
          <label htmlFor="search-destination" className="eyebrow block mb-2">Where</label>
          <div style={{ position: 'relative' }}>
            <input
              {...register('destination')}
              id="search-destination"
              type="text"
              placeholder="City, region, or landmark"
              className="field w-full"
              style={{ paddingRight: '2.5rem' }}
              onChange={handleDestinationChange}
            />
            {loadingSuggestions && (
              <div
                style={{
                  position: 'absolute',
                  right: '0.875rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
              >
                <svg
                  className="animate-spin h-4 w-4"
                  style={{ color: 'var(--primary)' }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>
          <input {...register('destinationId')} type="hidden" />
          {showSuggestions && (
            <div
              className="lift"
              style={{
                position: 'absolute',
                top: 'calc(100% + 0.35rem)',
                left: 0,
                right: 0,
                zIndex: 50,
                background: 'var(--surface-lowest)',
                borderRadius: '0.75rem',
                overflow: 'hidden',
              }}
            >
              {suggestions.map((place) => (
                <button
                  key={place.identifier}
                  type="button"
                  className="w-full text-left px-4 py-3 transition-colors"
                  style={{ background: 'transparent', color: 'var(--on-surface)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-low)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  onMouseDown={() => handleSuggestionSelect(place)}
                >
                  <div className="text-sm font-medium">{place.name}</div>
                  {place.description && (
                    <div className="text-xs mt-0.5" style={{ color: 'var(--on-surface-variant)' }}>
                      {place.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="search-checkin" className="eyebrow block mb-2">Check-in</label>
          <input
            {...register('ds')}
            id="search-checkin"
            type="date"
            className="field w-full"
          />
        </div>

        <div>
          <label htmlFor="search-checkout" className="eyebrow block mb-2">Check-out</label>
          <input
            {...register('de')}
            id="search-checkout"
            type="date"
            min={checkIn || undefined}
            className="field w-full"
          />
        </div>

        <div ref={guestDropdownRef} style={{ position: 'relative' }}>
          <label className="eyebrow block mb-2">Guests</label>
          <button
            type="button"
            className="field w-full flex justify-between items-center cursor-pointer text-left"
            onClick={() => setGuestDropdownOpen((o) => !o)}
          >
            <span className="text-sm" style={{ color: 'var(--on-surface)' }}>{guestLabel}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="currentColor"
              style={{ color: 'var(--on-surface-variant)' }}
            >
              <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708 0l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
          {guestDropdownOpen && (
            <div
              className="lift"
              style={{
                position: 'absolute',
                top: 'calc(100% + 0.35rem)',
                right: 0,
                zIndex: 50,
                background: 'var(--surface-lowest)',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                width: '19rem',
              }}
            >
              <GuestCounter
                title="Adults"
                subtitle="Ages 13+"
                value={adults}
                onDecrement={() => changeGuests('adults', -1)}
                onIncrement={() => changeGuests('adults', 1)}
              />
              <div className="mt-5">
                <GuestCounter
                  title="Children"
                  subtitle="Ages 2–12"
                  value={children}
                  onDecrement={() => changeGuests('children', -1)}
                  onIncrement={() => changeGuests('children', 1)}
                />
              </div>
              <div className="mt-5">
                <GuestCounter
                  title="Pets"
                  subtitle="Furry companions"
                  value={pets}
                  onDecrement={() => changeGuests('pets', -1)}
                  onIncrement={() => changeGuests('pets', 1)}
                />
              </div>
              <input {...register('ad')} type="hidden" />
              <input {...register('ch')} type="hidden" />
              <input {...register('pt')} type="hidden" />
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="tertiary text-sm"
                  onClick={() => setGuestDropdownOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 flex justify-end pt-2">
          <button type="submit" className="pill-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search stays
          </button>
        </div>
      </form>
    </div>
  );
}

interface GuestCounterProps {
  title: string;
  subtitle: string;
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
}

function GuestCounter({ title, subtitle, value, onDecrement, onIncrement }: GuestCounterProps) {
  return (
    <div className="flex justify-between items-center">
      <div>
        <div className="text-sm font-medium" style={{ color: 'var(--on-surface)' }}>{title}</div>
        <div className="text-xs mt-0.5" style={{ color: 'var(--on-surface-variant)' }}>{subtitle}</div>
      </div>
      <div className="flex items-center gap-3">
        <CounterButton onClick={onDecrement} label="−" />
        <span
          className="text-sm font-medium text-center"
          style={{ width: '1.75rem', color: 'var(--on-surface)' }}
        >
          {value}
        </span>
        <CounterButton onClick={onIncrement} label="+" />
      </div>
    </div>
  );
}

function CounterButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center transition-colors"
      style={{
        width: '2rem',
        height: '2rem',
        borderRadius: '9999px',
        background: 'var(--surface-high)',
        color: 'var(--on-surface)',
        fontSize: '1rem',
        lineHeight: 1,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-container)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-high)')}
    >
      {label}
    </button>
  );
}
